import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, Building2, Users, BedDouble, CalendarClock, MessageSquareWarning, Inbox } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cc_admin_token";

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 font-sans text-base text-navy placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30 transition-colors duration-300";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="mt-0.5 w-4 h-4 shrink-0 text-gold-deep" />
    <div className="min-w-0">
      <p className="font-sans text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">{label}</p>
      <p className="font-sans text-sm text-navy break-words">{value}</p>
    </div>
  </div>
);

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${t}` } });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setError(e.response?.data?.detail || "Please sign in again.");
      }
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
  };

  if (!token) {
    return (
      <section className="bg-skybrand-50 min-h-[70vh] flex items-center">
        <div className="max-w-md mx-auto w-full px-4 sm:px-6 py-20">
          <Reveal>
            <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-8 sm:p-10">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold-wash border border-gold-border">
                <Lock className="w-5 h-5 text-gold-deep" />
              </span>
              <h1 className="mt-5 text-center font-serif text-2xl sm:text-3xl font-semibold text-navy">Submissions Inbox</h1>
              <p className="mt-2 text-center font-sans text-sm text-slate-500">Private — authorized staff only.</p>
              <form onSubmit={login} className="mt-7 space-y-4" data-testid="admin-login-form">
                <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className={inputCls} data-testid="admin-email-input" aria-label="Email" />
                <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className={inputCls} data-testid="admin-password-input" aria-label="Password" />
                {error && (
                  <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 font-sans text-sm text-red-700" data-testid="admin-login-error">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={loading} data-testid="admin-login-button"
                  className="w-full rounded-full bg-gold px-6 py-3.5 font-sans text-base font-semibold text-navy-dark transition-colors duration-300 hover:bg-gold-hover disabled:opacity-60">
                  {loading ? "Signing in…" : "Sign In"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-skybrand-50 min-h-[70vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Private</Eyebrow>
            <h1 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">Submissions Inbox</h1>
            <p className="mt-2 font-sans text-sm text-slate-500">
              {bookings ? `${bookings.length} request${bookings.length === 1 ? "" : "s"}` : "Loading…"}
            </p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2.5 font-sans text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>

        {bookings && bookings.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-gold-border bg-white p-12 text-center" data-testid="admin-empty-state">
            <Inbox className="mx-auto w-10 h-10 text-gold" />
            <p className="mt-4 font-serif text-xl text-navy">No requests yet.</p>
            <p className="mt-1 font-sans text-sm text-slate-500">New requests will appear here.</p>
          </div>
        )}

        <div className="mt-8 space-y-5" data-testid="admin-bookings-list">
          {bookings?.map((b, i) => (
            <Reveal key={b.id} delay={Math.min(i, 5) * 0.05}>
              <article className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid={`admin-booking-card-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-serif text-xl sm:text-2xl font-semibold text-navy">{b.facility_name}</h2>
                    <p className="mt-1 font-sans text-sm text-slate-600">{b.name} · {b.role}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-sans text-xs text-slate-500">{fmtDate(b.created_at)}</p>
                    <span className={`mt-1 inline-block rounded-full px-3 py-1 font-sans text-[11px] font-semibold ${
                      b.email_sent ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"
                    }`} data-testid={`admin-booking-email-status-${i}`}>
                      {b.email_sent ? "Email notification sent" : "Saved (email pending)"}
                    </span>
                  </div>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  {b.business_type && <Row icon={Building2} label="Type of business" value={b.business_type} />}
                  {b.org_type && <Row icon={Building2} label="Organization" value={b.org_type} />}
                  {b.facility_type && <Row icon={Building2} label="Facility type" value={b.facility_type} />}
                  {b.beds && <Row icon={BedDouble} label="Beds" value={b.beds} />}
                  {b.preferred_format && <Row icon={CalendarClock} label="Preferred format" value={b.preferred_format} />}
                  <Row icon={Phone} label="Phone" value={b.phone} />
                  <Row icon={Mail} label="Email" value={b.email} />
                  <Row icon={Users} label="Contact" value={`${b.name} (${b.role})`} />
                </div>
                <div className="mt-6 rounded-xl bg-skybrand-50 border-l-4 border-gold px-5 py-4">
                  <p className="flex items-center gap-2 font-sans text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">
                    <MessageSquareWarning className="w-4 h-4 text-gold-deep" /> Where care is slipping
                  </p>
                  <p className="mt-2 font-sans text-sm sm:text-base leading-relaxed text-slate-700">{b.concern}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Admin;
